import { useMemo, useState } from 'react';
import { Box, Typography, Grid, FormHelperText } from '@mui/material';

import {
  FBoxContainer,
  FHAutocomplete,
  FHButton,
  FHInputField,
  FHPageLink,
  FProgressBar,
} from '../../../components/base';
import PageContainer from '../../../components/layout/PageContainer';
import { FORTA_ERROR } from '../../../utils/colors';
import SubmitCohortPasswordModal from './components/submitCohortPasswordModal';
import { useAddCohortForm, useAddCohortServiceClasses } from './hooks';
import { CHOOSE_SERVICE_CLASS_PLACEHOLDER, COHORT_FIELD_KEYS } from './constants/general';

import type { ChangeEvent, FormEvent, SyntheticEvent } from 'react';
import type { IOption } from '../../../utils/types';

function AddCohortForm() {
  const [submitPasswordModalOpen, setSubmitPasswordModalOpen] = useState(false);
  const { cohortServiceClasses, serviceClassesLoading } = useAddCohortServiceClasses();
  const { values, errors, touched, submitForm, handleUpdateFieldValues } = useAddCohortForm();

  const cohortNameValue = values[COHORT_FIELD_KEYS.NAME];
  const cohortServiceClassesValues = values[COHORT_FIELD_KEYS.SERVICE_CLASSES];

  const isSubmitDisabled = useMemo(
    () => !cohortNameValue.trim() || cohortServiceClassesValues.length === 0,
    [cohortNameValue, cohortServiceClassesValues],
  );

  const serviceClassPlaceholder = useMemo(
    () => (cohortServiceClassesValues.length === 0 ? CHOOSE_SERVICE_CLASS_PLACEHOLDER : ''),
    [cohortServiceClassesValues],
  );

  const closeSubmitPasswordModal = () => {
    setSubmitPasswordModalOpen(false);
  };

  const handleFormSubmitAttempt = (e: FormEvent) => {
    e.preventDefault();
    if (!isSubmitDisabled) {
      setSubmitPasswordModalOpen(true);
    }
  };

  if (serviceClassesLoading) {
    return <FProgressBar />;
  }

  return (
    <PageContainer>
      <Box mt={3}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer sx={{ borderRadius: '20px', boxShadow: 'none' }}>
          <form onSubmit={handleFormSubmitAttempt} style={{ width: '100%' }}>
            <Grid container>
              <Grid item xs={12} md={10} lg={7}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography textAlign="left" variant="h4">
                      Add Cohort
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                  />
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign="left">Name: </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHInputField
                        placeholder="Enter cohort name"
                        value={cohortNameValue}
                        inputHeight={40}
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues('name', e.target.value);
                        }}
                        error={
                          touched[COHORT_FIELD_KEYS.NAME] && Boolean(errors[COHORT_FIELD_KEYS.NAME])
                        }
                      />
                      {touched[COHORT_FIELD_KEYS.NAME] && errors[COHORT_FIELD_KEYS.NAME] && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors[COHORT_FIELD_KEYS.NAME]?.toString()}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3}>
                      <Typography textAlign="left">Service classes:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <FHAutocomplete
                        multiple
                        disableCloseOnSelect
                        sx={{
                          width: '100%',
                          boxShadow: 'none',
                          '& .MuiOutlinedInput-input': {
                            minHeight: '40px',
                          },
                        }}
                        placeholder={serviceClassPlaceholder}
                        options={cohortServiceClasses}
                        value={cohortServiceClassesValues}
                        onChange={(_event: SyntheticEvent, newValue: IOption[] | null) => {
                          handleUpdateFieldValues('service_classes', newValue || []);
                        }}
                        error={
                          touched[COHORT_FIELD_KEYS.SERVICE_CLASSES] &&
                          Boolean(errors[COHORT_FIELD_KEYS.SERVICE_CLASSES])
                        }
                      />
                      {touched[COHORT_FIELD_KEYS.SERVICE_CLASSES] &&
                        errors[COHORT_FIELD_KEYS.SERVICE_CLASSES] && (
                          <FormHelperText
                            sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                          >
                            {errors[COHORT_FIELD_KEYS.SERVICE_CLASSES]?.toString()}
                          </FormHelperText>
                        )}
                    </Grid>
                  </Grid>
                </Box>
                <Box ml={1} mt={3} sx={{ display: 'flex' }}>
                  <FHButton type="submit" disabled={isSubmitDisabled}>
                    Submit
                  </FHButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FBoxContainer>
      </Box>
      <SubmitCohortPasswordModal
        open={submitPasswordModalOpen}
        onClose={closeSubmitPasswordModal}
        onPasswordVerified={() => {
          submitForm();
        }}
      />
    </PageContainer>
  );
}

export default AddCohortForm;
